import requestService from './requestService';
import { getData } from '../utils/localStorage';
import { isHeroBrandEnabled } from '../utils/heroBrandUtils';

const endpoints = {
  customerService: '/branding/customerService',
};

// The public endpoint is keyed by business type tag. Herotolk is the only brand the app
// treats specially, so the key is either 'herotolk' or nothing at all — the backend
// answers an empty key with the b2b defaults.
// Named businessType rather than brand because requestService adds a brand parameter of
// its own to every request, carrying the browser name.
const BUSINESS_TYPE_PARAM = 'businessType';
export const HEROTOLK_BUSINESS_TYPE_KEY = 'herotolk';

const STORAGE_KEY = 'customerServiceContacts';

// Fired once a fetch has stored fresh contacts, so screens already on display can pick
// them up. The codebase uses document events for this kind of late-arriving count or
// detail elsewhere (see updateInvoiceCount in containers/app.js).
export const CUSTOMER_SERVICE_CONTACTS_UPDATED = 'customerServiceContactsUpdated';

/**
 * The business type to ask the backend about. isHeroBrandEnabled() reads the URL first
 * and the signed-in user's business type second, so this works both before and after
 * login.
 * @returns {string} 'herotolk', or '' for the b2b default.
 */
export const getBusinessTypeKey = () => (isHeroBrandEnabled() ? HEROTOLK_BUSINESS_TYPE_KEY : '');

/**
 * The contacts last fetched for the business type in effect right now. Contacts cached
 * under a different one are ignored rather than shown, so signing in as a Herotolk user
 * does not leave the Tulka details on screen.
 * @returns {{businessType: string, phone: string|null, email: string|null}|null}
 */
export const getCachedCustomerServiceContacts = () => {
  const cached = getData(STORAGE_KEY);

  if (!cached || cached.businessType !== getBusinessTypeKey()) {
    return null;
  }
  return cached;
};

const pending = new Map();

/**
 * Fetch the business type's customer service phone number and email address, and cache
 * them for the sync getters in utils/customerService.
 * Never rejects: these details only decorate a few screens and have hardcoded fallbacks,
 * so a failure here must not disturb login, signup or password reset.
 * Concurrent calls for the same business type share one request.
 * @returns {Promise<{businessType: string, phone: string|null, email: string|null}|null>}
 */
export const fetchCustomerServiceContacts = async() => {
  const businessType = getBusinessTypeKey();

  if (pending.has(businessType)) {
    return pending.get(businessType);
  }

  const request = (async() => {
    try {
      const data = await requestService.get(
        endpoints.customerService,
        businessType ? `${BUSINESS_TYPE_PARAM}=${encodeURIComponent(businessType)}` : '',
        { apiType: 'platform-v2' }
      );

      const contacts = {
        businessType,
        phone: data?.phone || null,
        email: data?.email || null,
      };

      // Signing in can change the business type mid-flight, and the two requests share
      // one cache entry. A reply that is no longer about the current business type is
      // dropped, so it cannot overwrite the details actually on display.
      if (businessType === getBusinessTypeKey()) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(contacts));
        document.dispatchEvent(new CustomEvent(CUSTOMER_SERVICE_CONTACTS_UPDATED));
      }

      return contacts;
    } catch (e) {
      console.error('Failed to fetch customer service contacts.', e);
      return null;
    } finally {
      pending.delete(businessType);
    }
  })();

  pending.set(businessType, request);
  return request;
};
